import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FolderDraftsReview } from "../FolderDraftsReview";
import { FolderStructureChooser } from "../FolderStructureChooser";
import { FolderSuggestionErrorBanner } from "../FolderSuggestionErrorBanner";
import { FolderSuggestionHeader } from "../FolderSuggestionHeader";
import {
  buildFolderDrafts,
  listFolderStructures,
  toCurrentFolderNameByPlanId,
  toImportFolders,
} from "../utils";
import type {
  FolderDrafts,
  FolderStructure,
  FolderSuggestionStep,
} from "../types";
import type { FolderSuggestionStepsProps } from "./types";

export const FolderSuggestionSteps = ({
  suggestion,
  suggestError,
  planRecords,
  folderRecords,
  isImporting,
  onSuggestAgain,
  onClose,
  onApply,
}: FolderSuggestionStepsProps) => {
  const { t } = useTranslation();

  const [stepState, setStepState] = useState<FolderSuggestionStep>(() => ({
    step: "choose",
    // "By level" leads the switcher order, so it opens whenever the plans have levels.
    structure: listFolderStructures({ suggestion })[0],
    editedDrafts: null,
  }));

  const changeStructure = useCallback((structure: FolderStructure) => {
    setStepState((current) =>
      current.structure === structure
        ? current
        : { step: "choose", structure, editedDrafts: null },
    );
  }, []);

  const goToReview = useCallback(() => {
    setStepState((current) =>
      current.step === "review"
        ? current
        : {
            step: "review",
            structure: current.structure,
            drafts:
              current.editedDrafts ??
              buildFolderDrafts({ suggestion, structure: current.structure }),
            overwrite: false,
          },
    );
  }, [suggestion]);

  const goBack = useCallback(() => {
    setStepState((current) =>
      current.step === "choose"
        ? current
        : {
            step: "choose",
            structure: current.structure,
            editedDrafts: current.drafts,
          },
    );
  }, []);

  const changeDrafts = useCallback(
    (update: (drafts: FolderDrafts) => FolderDrafts) => {
      setStepState((current) =>
        current.step === "review"
          ? { ...current, drafts: update(current.drafts) }
          : current,
      );
    },
    [],
  );

  const changeOverwrite = useCallback((overwrite: boolean) => {
    setStepState((current) =>
      current.step === "review" ? { ...current, overwrite } : current,
    );
  }, []);

  const apply = useCallback(() => {
    if (stepState.step !== "review") return;
    onApply({
      folders: toImportFolders({ drafts: stepState.drafts }),
      overwrite: stepState.overwrite,
      structure: stepState.structure,
    });
  }, [stepState, onApply]);

  const structures = useMemo(
    () => listFolderStructures({ suggestion }),
    [suggestion],
  );

  const planNameById = useMemo(
    () => new Map(planRecords.map((plan) => [plan.id, plan.name])),
    [planRecords],
  );

  const currentFolderNameByPlanId = useMemo(
    () => toCurrentFolderNameByPlanId({ planRecords, folderRecords }),
    [planRecords, folderRecords],
  );

  return (
    <>
      <FolderSuggestionHeader
        subtitle={
          stepState.step === "choose"
            ? t("smartSurveyor.folders.suggestion.chooseSubtitle", {
                count: suggestion.plans.length,
              })
            : t("smartSurveyor.folders.suggestion.reviewSubtitle", {
                structure: t(
                  `smartSurveyor.folders.suggestion.structures.${stepState.structure}`,
                ),
              })
        }
        isBusy={isImporting}
        onSuggestAgain={
          stepState.step === "choose" ? onSuggestAgain : undefined
        }
        onClose={onClose}
      />
      {suggestError !== null && (
        <div className="mb-3">
          <FolderSuggestionErrorBanner
            error={suggestError}
            isLastResultKept
            onRetry={onSuggestAgain}
          />
        </div>
      )}
      {stepState.step === "choose" ? (
        <FolderStructureChooser
          suggestion={suggestion}
          structures={structures}
          structure={stepState.structure}
          planNameById={planNameById}
          onStructureChange={changeStructure}
          onNext={goToReview}
          onCancel={onClose}
        />
      ) : (
        <FolderDraftsReview
          drafts={stepState.drafts}
          overwrite={stepState.overwrite}
          planNameById={planNameById}
          currentFolderNameByPlanId={currentFolderNameByPlanId}
          isImporting={isImporting}
          onDraftsChange={changeDrafts}
          onOverwriteChange={changeOverwrite}
          onBack={goBack}
          onApply={apply}
        />
      )}
    </>
  );
};
